/**
 * Sends a file to the server in fixed-size parts. [AVP-891]
 *
 * nginx caps a request body at 20 MB and kills any request at 60 s, so a
 * 30 MB CSV cannot be posted in one go. Parts are addressed by index and the
 * server writes each one at its own offset, so re-sending a part is harmless
 * and a dropped connection is recovered by asking which parts already landed.
 *
 * `putPart` is injected rather than imported: this module does no networking,
 * which is what makes it testable without mocking axios.
 */

export interface IPartProgress {
  /** bytes of the whole file confirmed sent so far */
  sentBytes: number;
  totalBytes: number;
  /** 0-based index of the part currently in flight */
  partIndex: number;
  partCount: number;
}

export type TPutPart = (
  partIndex: number,
  chunk: Blob,
  onPartProgress: (loadedBytes: number) => void,
) => Promise<void>;

export interface IUploadInPartsOptions {
  file: Blob;
  partBytes: number;
  putPart: TPutPart;
  onProgress?: (progress: IPartProgress) => void;
  /** Polled before every part, so a cancel lands within one part. */
  isCancelled?: () => boolean;
  /** Part indexes the server already holds, from GET /uploads/{id}. */
  alreadyReceived?: number[];
}

export interface IUploadInPartsResult {
  cancelled: boolean;
  sentParts: number[];
  skippedParts: number[];
  partCount: number;
}

export const partCountOf = (totalBytes: number, partBytes: number): number => {
  if (partBytes <= 0) {
    throw new Error('partBytes must be greater than zero');
  }
  return Math.max(1, Math.ceil(totalBytes / partBytes));
};

export const uploadInParts = async (
  options: IUploadInPartsOptions,
): Promise<IUploadInPartsResult> => {
  const {
    file, partBytes, putPart, onProgress, isCancelled, alreadyReceived,
  } = options;

  const totalBytes = file.size;
  const partCount = partCountOf(totalBytes, partBytes);
  const skip = new Set<number>(alreadyReceived ?? []);

  const sentParts: number[] = [];
  const skippedParts: number[] = [];

  /*
   * Parts go one at a time rather than in parallel. The import worker shares a
   * connection pool with the rest of the service, and a serial upload gives a
   * progress bar that only ever moves forwards — a parallel one needs
   * reconciling every time a part finishes out of order.
   */
  for (let index = 0; index < partCount; index += 1) {
    if (isCancelled?.()) {
      return {
        cancelled: true, sentParts, skippedParts, partCount,
      };
    }

    const start = index * partBytes;
    const sentBefore = start;

    if (skip.has(index)) {
      skippedParts.push(index);
      onProgress?.({
        sentBytes: Math.min(start + partBytes, totalBytes), totalBytes, partIndex: index, partCount,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    const chunk = file.slice(start, Math.min(start + partBytes, totalBytes));

    // eslint-disable-next-line no-await-in-loop
    await putPart(index, chunk, (loadedBytes) => {
      onProgress?.({
        sentBytes: Math.min(sentBefore + loadedBytes, totalBytes),
        totalBytes,
        partIndex: index,
        partCount,
      });
    });

    sentParts.push(index);
    onProgress?.({
      sentBytes: Math.min(start + chunk.size, totalBytes), totalBytes, partIndex: index, partCount,
    });
  }

  return {
    cancelled: false, sentParts, skippedParts, partCount,
  };
};

export default uploadInParts;
